// The company's payment methods for Aldoo's commission on its cash on delivery sales (aldoo-na store/payouts/
// billing): the courier pays the vendor the whole sale, so the commission is the vendor's to give — taken from
// its online payouts first, and what they do not cover charged at the monthly settlement (or early, Payouts →
// Commission → Pay now) to the card, else the bank account (SEPA Direct Debit), else paid by hand from a proforma.
// None is required to sell with cash on delivery. Company → Payments.
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import { showErrorToast, showSuccessToast } from "aldoo-ra/Toast"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"
import { Chip, Field, Grid, TextInput, Toggle, HelpTip, MethodRow } from "../AdminShared/ui"
import { CreditCardIcon, BuildingLibraryIcon } from "@heroicons/react/24/outline"
import { CountryPicker } from "../AdminShared/CountriesSelect"
import { BillingAddressSave, BillingAttach, BillingCardRemove, BillingDebitPayoutAccount, BillingOverview, BillingSetup } from "../AdminShared/api"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { PAYOUTS } from "../Vendor/texts/bundles"
import { CardMark } from "../Storefront/checkout/PaymentLogos"
import { stripeErrorText } from "./stripeErrors"

/** The vendor's billing as the API says it (BillingOverview), reloaded on demand. */
// `refreshKey`: read again when it changes (the payout IBAN saved anew)
export function useBilling(orgId, refreshKey) {
  const [overview, setOverview] = useState(null)
  const reload = useCallback(async () => {
    if (!orgId) return null
    const res = await BillingOverview(orgId).catch(() => null)
    if (res && !res.error) setOverview(res)
    return res
  }, [orgId])
  useEffect(() => {
    reload()
  }, [reload, refreshKey])
  return { overview, reload, setOverview }
}

// The card for the commission on cash on delivery sales — charged at once (at the settlement, or Pay now), as an
// ads platform charges its card; without one the commission is paid by hand. The payout IBAN, debited (SEPA Direct Debit), only backs it
// when it is declined. false hides it (the IBAN the one way to pay).
export const CARD_ENABLED = true

const DAY_MS = 24 * 60 * 60 * 1000
const BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", jcb: "JCB", unionpay: "UnionPay", diners: "Diners Club" }
const dateText = (d, language) => (d ? new Date(d).toLocaleDateString(language === "bg" ? "bg-BG" : "en-GB", { timeZone: "Europe/Sofia", day: "numeric", month: "long", year: "numeric" }) : "")

/** Stripe's Payment Element over a SetupIntent: the card (or bank account and its mandate) saved, then made the company's. */
// Its save is the dialog's (the footer's buttons, outside Stripe's <Elements>): `actions` gets the
// submit, `onStatus` the form's state ({ busy, ready }) for the buttons.
function CardForm({ orgId, kind = "card", onSaved, actions, onStatus }) {
  const bank = kind === "bank"
  const t = usePortalTexts(PAYOUTS)
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  // Stripe's form loading (its fields come from Stripe): a loader over it until it is ready
  const [ready, setReady] = useState(false)
  const submit = async () => {
    if (!stripe || !elements) return
    setBusy(true)
    setError("")
    const { error: stripeError, setupIntent } = await stripe.confirmSetup({ elements, redirect: "if_required" })
    if (stripeError) {
      setBusy(false)
      // a field typed wrong (Stripe's form says it in the vendor's language): its own words; a refusal: ours
      if (stripeError.type === "validation_error") return setError(stripeError.message)
      return setError(stripeErrorText(t, stripeError.decline_code || stripeError.code, stripeError.message))
    }
    const res = await BillingAttach(orgId, setupIntent.payment_method).catch((e) => ({ error: e.message }))
    setBusy(false)
    if (!res?.result) return setError(res?.error ? stripeErrorText(t, res.error, res.message) : bank ? t`The bank account could not be saved` : t`The card could not be saved`)
    showSuccessToast(bank ? t`Bank account saved` : t`Card saved`)
    onSaved?.(res.overview)
  }
  if (actions) actions.current = submit
  useEffect(() => onStatus?.({ busy, ready: ready && !!stripe }), [busy, ready, stripe])
  return (
    <div className="flex flex-col gap-4" data-testid="billing-card-form">
      <div className="relative min-h-[120px]">
        <PaymentElement options={{ layout: "tabs" }} onReady={() => setReady(true)} onLoadError={() => setReady(true)} />
        {/* the form loading, or the card / bank account being saved with Stripe: a loader in the middle of it */}
        {(!ready || busy) && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-admin_paper/90 rounded-md" data-testid="billing-form-loading">
            <Spinner variant="admin_primary" size="lg" />
            <span className="text-sm text-admin_text_secondary">{busy ? (bank ? t`Saving the bank account…` : t`Saving the card…`) : t`Loading…`}</span>
          </div>
        )}
      </div>
      {error && <div className="rounded-md px-3 py-2 text-sm bg-admin_error/10 text-admin_error" data-testid="billing-card-error">{error}</div>}
    </div>
  )
}

// `kind`: "card", or "bank" — a bank account debited by SEPA Direct Debit (Stripe's form asks the IBAN and the mandate)
function CardDialog({ orgId, kind = "card", onClose, onSaved }) {
  const t = usePortalTexts(PAYOUTS)
  const language = useSiteLanguage()
  const bank = kind === "bank"
  const [secret, setSecret] = useState(null)
  const [error, setError] = useState("")
  // the form's save, and its state, for the footer's buttons
  const actions = useRef(null)
  const [status, setStatus] = useState({ busy: false, ready: false })
  useEffect(() => {
    BillingSetup(orgId, kind)
      .then((res) => (res?.clientSecret ? setSecret(res.clientSecret) : setError(res?.message || t`Card payments are not available right now`)))
      .catch((e) => setError(e.message))
  }, [orgId])
  return (
    <Modal isOpen onClose={onClose} variant="admin" closeOnEscape title={
        // what it is used for, behind the (?) beside the title
        <span className="inline-flex items-center gap-2">
          {bank ? t`Bank account for the commission on cash on delivery` : t`Card for the commission on cash on delivery`}
          <HelpTip
            label={bank ? t`Bank account for the commission on cash on delivery` : t`Card for the commission on cash on delivery`}
            text={bank ? t`Debited (SEPA Direct Debit) only for Aldoo's commission on your completed cash on delivery orders, when your online sales do not cover it and there is no valid card. A debit takes a few business days to confirm.` : t`Charged only for Aldoo's commission on your completed cash on delivery orders that your online payouts do not cover: on the settlement day each month, or at once when you pay now.`}
            testId="billing-dialog-help"
          />
        </span>
      } styleOverrides={{ container: "w-[min(94vw,560px)] h-[min(90vh,820px)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden", footer: "shrink-0 px-5 py-3 border-t border-admin_bordered bg-admin_paper" }}
      footerContent={
        // always in sight: Stripe's form (the IBAN, the address, the mandate) scrolls above them
        <div className="flex gap-2 justify-end">
          <Button variant="admin_secondary" onClick={onClose} disabled={status.busy}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary" onClick={() => actions.current?.()} disabled={!secret || status.busy || !status.ready} data-testid="billing-card-save">
            <span className="inline-flex items-center gap-2">
              {status.busy && <Spinner variant="white" size="sm" />}
              {status.busy ? t`Saving…` : bank ? t`Save the bank account` : t`Save the card`}
            </span>
          </Button>
        </div>
      }>
      <div className="p-5 pt-1 flex flex-col gap-4">
        {error && <div className="text-sm text-admin_error">{error}</div>}
        {secret && (
          <Elements stripe={getStripePromise()} options={{ clientSecret: secret, appearance: { theme: "stripe" }, locale: language === "bg" ? "bg" : "en" }}>
            <CardForm orgId={orgId} kind={kind} onSaved={onSaved} actions={actions} onStatus={setStatus} />
          </Elements>
        )}
        {/* asking Stripe for the form */}
        {!secret && !error && (
          <div className="min-h-[160px] flex flex-col items-center justify-center gap-3" data-testid="billing-setup-loading">
            <Spinner variant="admin_primary" size="lg" />
            <span className="text-sm text-admin_text_secondary">{t`Loading…`}</span>
          </div>
        )}
      </div>
    </Modal>
  )
}

const ADDRESS = ["line1", "line2", "city", "postalCode", "country"]

/** Where Aldoo's invoices are addressed: the company's address, or a separate billing address. */
function BillingAddress({ orgId, overview, canEdit, onChanged }) {
  const t = usePortalTexts(PAYOUTS)
  const language = useSiteLanguage()
  const separate = overview.addressMode === "separate"
  const [draft, setDraft] = useState(() => Object.fromEntries(ADDRESS.map((k) => [k, overview.address?.[k] || ""])))
  const [open, setOpen] = useState(separate)
  const [busy, setBusy] = useState(false)
  const save = async (mode, address) => {
    setBusy(true)
    const res = await BillingAddressSave(orgId, mode, address).catch((e) => ({ error: e.message }))
    setBusy(false)
    if (!res?.result) return showErrorToast(t`The billing address could not be saved`, res?.message)
    showSuccessToast(t`Billing address saved`)
    onChanged?.()
  }
  const same = !open
  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }))
  return (
    <div className="flex flex-col gap-3" data-testid="billing-address">
      <Toggle
        checked={same}
        disabled={!canEdit || busy}
        onChange={(on) => {
          setOpen(!on)
          if (on && separate) save("company")
        }}
        label={t`The billing address is the company's address`}
        data-testid="billing-address-same"
      />
      {open && (
        <>
          <Grid cols={2}>
            <Field label={t`Billing address`}>
              <TextInput value={draft.line1} onChange={set("line1")} disabled={!canEdit} data-testid="billing-line1" />
            </Field>
            <Field label={t`Address, line 2 (optional)`}>
              <TextInput value={draft.line2} onChange={set("line2")} disabled={!canEdit} />
            </Field>
            <Field label={t`City`}>
              <TextInput value={draft.city} onChange={set("city")} disabled={!canEdit} data-testid="billing-city" />
            </Field>
            <Field label={t`Postal code`}>
              <TextInput value={draft.postalCode} onChange={set("postalCode")} disabled={!canEdit} />
            </Field>
            <Field label={t`Country`}>
              <CountryPicker value={draft.country} onChange={set("country")} language={language} disabled={!canEdit} placeholder={t`Pick a country…`} />
            </Field>
          </Grid>
          {canEdit && (
            <div>
              <Button variant="admin_primary" onClick={() => save("separate", draft)} disabled={busy || !draft.line1 || !draft.city || !draft.country} data-testid="billing-address-save">
                {busy ? t`Saving…` : t`Save the billing address`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * A numbered step of Company → Payments, as a chip: (1) the bank account Aldoo pays into,
 * (2) the card the commission on cash on delivery is paid with.
 */
export function PaymentStep({ n, title, hint, help, error, children, testId }) {
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="flex flex-col gap-1">
        <span className="self-start inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-admin_primary/10 text-admin_primary pl-1 pr-3 py-1 text-sm font-semibold">
            {n ? <span className="w-6 h-6 rounded-full bg-admin_primary text-white flex items-center justify-center text-xs font-bold">{n}</span> : <span className="w-1" />}
            {title}
          </span>
          {help && <HelpTip text={help} label={title} />}
        </span>
        {hint && <span className="text-sm text-admin_text_secondary">{hint}</span>}
        {error && <span className="text-xs text-admin_error" data-testid={testId ? `${testId}-error` : undefined}>{error}</span>}
      </div>
      {children}
    </div>
  )
}

/**
 * Company → Payments. One bank account: Aldoo pays the sales into it and, once its mandate is ticked,
 * debits from it the commission on cash on delivery sales. With the card (CARD_ENABLED): (1) the bank
 * account, its debits backing the card, (2) the card the commission is charged to — optional: without one the
 * commission is paid by hand (a manual payment, from its proforma).
 */
// payoutMissing: no way to be paid yet — "stripe" when the Stripe account is offered beside the bank account, else true
export function PaymentsSteps({ bankAccount, orgId, canEdit, codOn, payoutIban, payoutSavedAt, payoutMissing = false }) {
  const t = usePortalTexts(PAYOUTS)
  // one way to be paid is required, said for the step as a whole (not under one of its accounts)
  const payoutError = payoutMissing ? (payoutMissing === "stripe" ? t`Required: connect your Stripe account or add a bank account` : t`Required`) : null
  if (!CARD_ENABLED)
    return (
      <div className="flex flex-col gap-4" data-testid="payments-steps">
        <PaymentStep title={t`Payouts`} hint={t`Where Aldoo pays your sales and debits its commission on cash on delivery sales`} help={DEBIT_HELP(t)} error={payoutError} testId="payments-step-bank">
          {bankAccount}
        </PaymentStep>
        <CommissionDebit orgId={orgId} canEdit={canEdit} codOn={codOn} payoutIban={payoutIban} payoutSavedAt={payoutSavedAt} />
      </div>
    )
  return (
    <div className="flex flex-col gap-6" data-testid="payments-steps">
      {/* where the sales are paid: the company's Stripe account first when it connected one, else its bank account */}
      <PaymentStep n={1} title={t`Payouts`} hint={t`Where Aldoo pays your sales: your Stripe account when you connect one, otherwise your bank account`} error={payoutError} testId="payments-step-bank">
        {bankAccount}
        {/* the debits backing the card (CommissionDebit backsCard): hidden for now — the card alone pays the commission */}
      </PaymentStep>
      <div className="border-t border-admin_bordered" />
      <PaymentStep
        n={2}
        title={t`Card for the commission`}
        hint={t`Optional: without a card, the commission is paid by hand, from a proforma`}
        help={CARD_HELP(t)}
        testId="payments-step-card"
      >
        <BillingCard orgId={orgId} canEdit={canEdit} codOn={codOn} inStep showBank={false} />
      </PaymentStep>
    </div>
  )
}

const CARD_HELP = (t) =>
  t`Buyers who pay on delivery pay the courier, and the courier pays you the whole amount. Aldoo's commission on those orders is taken from your online payouts first; what they do not cover — all of it when you sell only with cash on delivery — is charged to this card on the settlement day each month, or at once when you pay now. If the card is declined, it is debited from your bank account instead. The card is used only for this.`

const DEBIT_HELP = (t) =>
  t`Buyers who pay on delivery pay the courier, and the courier pays you the whole amount. Aldoo's commission on those orders is taken from your online payouts first; what they do not cover — all of it when you sell only with cash on delivery — is debited from this bank account (SEPA Direct Debit) on the settlement day each month, or earlier when you pay now. A debit takes a few business days to confirm. The account is debited only for this.`

/**
 * The payout bank account debited for the commission on cash on delivery sales: saving the IBAN accepts
 * the SEPA mandate (the form says so), so the account Aldoo pays into is the one it debits — no second
 * IBAN, no switch to stop it (the commission owed must be collected), nothing shown while they are on.
 * "Allow the debits" only for an IBAN saved before, or typed in by Aldoo for the vendor; the hold, and
 * the billing address.
 */
// `backsCard`: the card is the commission's (CARD_ENABLED) — the debits only back it when it is declined
export function CommissionDebit({ orgId, canEdit, codOn = true, payoutIban, payoutSavedAt, backsCard = false }) {
  const t = usePortalTexts(PAYOUTS)
  // read again when the IBAN is saved (its debits set up with it)
  const { overview, reload, setOverview } = useBilling(orgId, `${payoutIban}|${payoutSavedAt || ""}`)
  const [busy, setBusy] = useState(false)
  // no payout IBAN yet: nothing to debit — the bank account above comes first
  if (!payoutIban) return null
  if (!overview)
    return (
      <div className="min-h-[80px] flex items-center justify-center gap-3 text-sm text-admin_text_secondary" data-testid="billing-loading">
        <Spinner variant="admin_primary" size="md" /> {t`Loading…`}
      </div>
    )
  const bank = overview.bank
  // the payout account is the one debited (and still is: not changed since its mandate)
  const debiting = !!bank && !overview.payoutAccountChanged
  const holdText = overview.hold?.reason === "charge_failed" ? t`Cash on delivery is on hold: the commission could not be debited. Check the bank account or pay what is owed (Payouts → Commission).` : null

  const allow = async () => {
    setBusy(true)
    const res = await BillingDebitPayoutAccount(orgId).catch((e) => ({ error: e.message }))
    setBusy(false)
    if (!res?.result) {
      reload()
      return showErrorToast(t`The bank account could not be set up for debits`, res?.error ? stripeErrorText(t, res.error, res.message) : res?.message)
    }
    if (res.overview) setOverview(res.overview)
    else reload()
    showSuccessToast(t`Aldoo's commission will be debited from this account`)
  }
  return (
    <div className="flex flex-col gap-3" data-testid="commission-debit">
      {holdText && <div className="rounded-lg px-4 py-3 text-sm bg-admin_error/10 text-admin_error" data-testid="billing-hold">{holdText}</div>}
      {/* the debits come with the IBAN (saving it accepts their mandate): nothing to show while they are on.
          Only an account saved before that (or typed in by Aldoo for the vendor) asks for them, here, once */}
      {/* Stripe refused the account for debits: why, in the vendor's language */}
      {!debiting && overview.debitSetupError && (
        <div className="rounded-lg px-4 py-3 text-sm bg-admin_error/10 text-admin_error" data-testid="commission-debit-error">
          <span className="font-semibold">{t`This bank account cannot be debited:`}</span> {stripeErrorText(t, overview.debitSetupError.code, overview.debitSetupError.message)}
        </div>
      )}
      {!debiting && !(backsCard && overview.debitSetupError) && (
        <div className={`rounded-lg px-4 py-3 flex flex-col gap-2 text-sm ${backsCard ? "bg-admin_neutral_1 border border-admin_bordered" : "bg-admin_warning/15"} text-admin_text`} data-testid={backsCard ? "commission-debit-offer" : "billing-card-required"}>
          <span>{overview.payoutAccountChanged ? t`Your bank account changed: allow the debits again so the commission is debited from the new one.` : backsCard ? t`Optional: if your card is declined, Aldoo can debit the commission from this account instead.` : t`Optional: allowing the debits lets Aldoo take the commission from this account; without them it is paid by hand, from a proforma.`}</span>
          {overview.payoutAccountDebitable ? (
            <>
              <p className="m-0 text-xs leading-relaxed text-admin_text_secondary" data-testid="commission-debit-mandate">
                {t`By clicking "Allow the debits", you authorise Aldoo and Stripe, our payment service provider, to send instructions to your bank to debit your account, and your bank to debit your account in accordance with those instructions. As part of your rights, you are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within 8 weeks starting from the date on which your account was debited.`}
              </p>
              {canEdit && (
                <Button variant="admin_primary" onClick={allow} disabled={busy} className="self-start" data-testid="commission-debit-allow">
                  <span className="inline-flex items-center gap-2">
                    {busy && <Spinner variant="white" size="sm" />}
                    {busy ? t`Saving…` : t`Allow the debits`}
                  </span>
                </Button>
              )}
            </>
          ) : (
            <span className="text-xs text-admin_error">{t`Enter the bank account again (Change) to allow the debits.`}</span>
          )}
        </div>
      )}
      {/* with the card, its step holds the billing address */}
      {!backsCard && <BillingAddress orgId={orgId} overview={overview} canEdit={canEdit} onChanged={reload} />}
    </div>
  )
}

/**
 * The commission's payment methods: the card and the bank account (SEPA Direct Debit) — each added, replaced,
 * removed — the card tried first, the bank account when there is no valid card or it is declined; the hold;
 * the billing address.
 */
// `showBank`: the bank account row (false: the payout IBAN backs the card, Company → Payments step 1)
export function BillingCard({ orgId, canEdit, codOn = true, inStep = false, showBank = true }) {
  const t = usePortalTexts(PAYOUTS)
  const language = useSiteLanguage()
  const { overview, reload, setOverview } = useBilling(orgId)
  // the setup dialog open: "card" or "bank"
  const [dialog, setDialog] = useState(null)
  const [removing, setRemoving] = useState(null)
  // the company's payment methods being read (from Stripe and the store): a loader in their place
  if (!overview)
    return (
      <div className="min-h-[140px] flex items-center justify-center gap-3 text-sm text-admin_text_secondary" data-testid="billing-loading">
        <Spinner variant="admin_primary" size="md" /> {t`Loading…`}
      </div>
    )
  const card = overview.card
  const bankAccount = overview.bank
  const none = !card && !bankAccount
  // taken off, asked first: with the other method left it pays; with none, the commission on cash on delivery
  // sales is taken from the online payouts and the rest paid by hand (cash on delivery stays on)
  const remove = async (kind) => {
    const bank = kind === "bank"
    const otherLeft = bank ? !!card : !!bankAccount
    const message = otherLeft
      ? bank
        ? t`The bank account is removed from your account. The commission is charged to your card.`
        : t`The card is removed from your account. The commission is debited from your bank account.`
      : t`The payment method is removed from your account. Aldoo's commission on cash on delivery sales is still taken from your online payouts.`
    const title = bank ? t("Remove the bank account •••• %s?", bankAccount.last4) : t("Remove the card •••• %s?", card.last4)
    const ok = await showPromptDialogAsync({ variant: "admin", title, message, confirmText: bank ? t`Remove the bank account` : t`Remove the card`, cancelText: t`Cancel` })
    if (!ok) return
    setRemoving(kind)
    const res = await BillingCardRemove(orgId, kind).catch((e) => ({ error: e.message }))
    setRemoving(null)
    if (!res?.result) return showErrorToast(bank ? t`The bank account could not be removed` : t`The card could not be removed`, res?.error ? removeErrorText(res) : res?.message)
    if (res.overview) setOverview(res.overview)
    else reload()
    showSuccessToast(bank ? t`Bank account removed` : t`Card removed`)
  }
  const removeErrorText = (res) => (res.error === "owed_now" ? t`Pay the commission that is due first (Payouts → Commission), or replace the card.` : res.error === "charge_open" ? t`A payment of the commission is still open: let it finish, or pay it first.` : res.message)
  const expiringSoon = card?.valid && new Date(card.expiresAt).getTime() - Date.now() < 30 * DAY_MS
  // an expired card does not stop cash on delivery (the commission is then paid by hand: said below the card)
  const holdText = overview.hold?.reason === "manual_overdue" ? t`Cash on delivery is on hold: the commission was not paid in time.` : overview.hold?.reason === "charge_failed" ? t`Cash on delivery is on hold: the commission could not be charged. Update the payment method or pay what is owed (Payouts → Commission).` : null
  return (
    <div className={`flex flex-col gap-4 ${inStep ? "" : "border-t border-admin_bordered pt-5 mt-1"}`} data-testid="billing-card">
      {/* in Company → Payments the step says it (its (?)); on its own, the title and the same (?) */}
      {!inStep && (
        <div className="flex items-center gap-2 text-base font-semibold text-admin_text">
          {t`Payment method for the commission on cash on delivery`}
          <HelpTip label={t`Payment method for the commission on cash on delivery`} text={t`Buyers who pay on delivery pay the courier, and the courier pays you the whole amount. Aldoo's commission on those orders is taken from your online payouts first; what they do not cover is charged on the settlement day each month (or earlier, when you pay now) — to your card, or, without a valid card or when it is declined, debited from your bank account. They are used only for this.`} />
        </div>
      )}
      {holdText && <div className="rounded-lg px-4 py-3 text-sm bg-admin_error/10 text-admin_error" data-testid="billing-hold">{holdText}</div>}
      {/* an expired card: the bank account, else a payment by hand, pays the commission (cash on delivery stays on) */}
      {card && !card.valid && codOn && <div className="rounded-lg px-4 py-3 text-sm bg-admin_neutral_1 text-admin_text" data-testid="billing-card-required">{t`The card expired: add a new one, or the commission is paid by hand, from a proforma.`}</div>}

      {/* the card: tried first */}
      <MethodRow
        icon={CreditCardIcon}
        title={t`Card`}
        help={showBank ? t`Charged first: at the monthly settlement, or when you pay now.` : t`Charged at the monthly settlement, or when you pay manually.`}
        testId="billing-method-card"
        value={
          card ? (
            <span data-testid="billing-card-current" className="flex items-center gap-2">
              <CardMark brand={card.brand} className="-ml-1.5" />
              <span className="font-semibold">{BRANDS[card.brand] || card.brand}</span>
              <span className="font-mono tracking-wider">•••• {card.last4}</span>
              <span className="text-admin_text_secondary">{String(card.expMonth).padStart(2, "0")}/{String(card.expYear).slice(-2)}</span>
            </span>
          ) : null
        }
        empty={t`No card yet`}
        chip={
          card ? (
            !card.valid ? (
              <Chip className="bg-admin_error/10 text-admin_error">{overview.graceUntil ? t("Expired — add a new card by %s", dateText(overview.graceUntil, language)) : t`Expired`}</Chip>
            ) : expiringSoon ? (
              <Chip className="bg-admin_warning/20 text-admin_text">{t("Expires on %s", dateText(card.expiresAt, language))}</Chip>
            ) : (
              <Chip className="bg-admin_success/10 text-admin_success">{t`Active`}</Chip>
            )
          ) : null
        }
        actions={
          canEdit && (
            <>
              <Button variant={card ? "admin_secondary" : "admin_primary"} onClick={() => setDialog("card")} data-testid="billing-card-add">
                {card ? t`Replace` : t`Add a card`}
              </Button>
              {card && (
                <Button variant="admin_text" onClick={() => remove("card")} disabled={!!removing} className="text-admin_error" data-testid="billing-card-remove">
                  {removing === "card" ? <span className="inline-flex items-center gap-1.5"><Spinner variant="admin_primary" size="sm" />{t`Removing…`}</span> : t`Remove`}
                </Button>
              )}
            </>
          )
        }
      />

      {/* the bank account: debited when there is no valid card, or it is declined */}
      {showBank && <MethodRow
        icon={BuildingLibraryIcon}
        title={t`Bank account`}
        help={t`SEPA Direct Debit: debited when there is no valid card or the card is declined. A bank debit takes a few business days to confirm.`}
        testId="billing-method-bank"
        value={
          bankAccount ? (
            <span data-testid="billing-bank-current" className="flex items-center gap-2">
              <span className="font-semibold">IBAN</span>
              <span className="font-mono tracking-wider">{bankAccount.country || ""} •••• {bankAccount.last4}</span>
            </span>
          ) : null
        }
        empty={t`No bank account yet`}
        chip={bankAccount ? <Chip className="bg-admin_success/10 text-admin_success">{card?.valid ? t`If the card fails` : t`Active`}</Chip> : null}
        actions={
          canEdit && (
            <>
              <Button variant={bankAccount || card ? "admin_secondary" : "admin_primary"} onClick={() => setDialog("bank")} data-testid="billing-bank-add">
                {bankAccount ? t`Replace` : t`Add a bank account`}
              </Button>
              {bankAccount && (
                <Button variant="admin_text" onClick={() => remove("bank")} disabled={!!removing} className="text-admin_error" data-testid="billing-bank-remove">
                  {removing === "bank" ? <span className="inline-flex items-center gap-1.5"><Spinner variant="admin_primary" size="sm" />{t`Removing…`}</span> : t`Remove`}
                </Button>
              )}
            </>
          )
        }
      />}

      <BillingAddress orgId={orgId} overview={overview} canEdit={canEdit} onChanged={reload} />
      {dialog && (
        <CardDialog
          orgId={orgId}
          kind={dialog}
          onClose={() => setDialog(null)}
          onSaved={(next) => {
            setDialog(null)
            if (next) setOverview(next)
            else reload()
          }}
        />
      )}
    </div>
  )
}

export default BillingCard
